import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { IJourney, ScreenConfig } from "../../interfaces/journey";
import { useJourney } from "../../hooks/useJourney";
import Sidebar from "../Sidebar/Sidebar";
import { AutoCompleteClient } from "../AutoComplete/AutoCompleteClient";
import { AutoCompleteProduct } from "../AutoComplete/AutoCompleteProduct";
import PhonePreview from "../PhonePreview/PhonePreview";
import { Modal } from "react-bootstrap";
import { resolveLogoUrl } from "../../Utils/resolveLogoUrl";
import { ScreenSelector } from "../ScreenSelector/ScreenSelector";
import StyledLabel from "../Label/StyledLabel";

interface AddJourneyProps {
  Journey?: IJourney;
}

const JourneyUIConfig: React.FC<AddJourneyProps> = ({ Journey }) => {
  const { handleSave, isSaving, checkIfExists } = useJourney();

  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingData, setPendingData] = useState<IJourney | null>(null);

  const defaultScreens: ScreenConfig[] = [
    { id: "cpf", enabled: true },
    { id: "location", enabled: true },
    { id: "face", enabled: true },
    { id: "document", enabled: true },
    { id: "documentoscopia", enabled: true },
    { id: "facematch", enabled: true },
    { id: "bureau", enabled: true },
  ];

  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<IJourney>({
    defaultValues: {
      backgroundColor: Journey?.backgroundColor || "#ffffff",
      textColor: Journey?.textColor || "#000000",
      buttonColor: Journey?.buttonColor || "#78E01D",
      buttonLabelColor: Journey?.buttonLabelColor || "#ffffff",
      clientName: Journey?.clientName || "",
      productId: Journey?.productId || undefined,
      productName: Journey?.productName || "",
      clientId: Journey?.clientId || 0,
      logoUrl: Journey?.logoUrl || "",
      id: Journey?.id,
      labelColor: Journey?.labelColor || "#000000",
      screens: Journey?.screens.length > 0 ? Journey?.screens : defaultScreens,
      initialText: Journey?.initialText,
      finalText: Journey?.finalText,
      redirectLink: Journey?.redirectLink
    },
  });

  const [selectedClient, setSelectedClient] = useState<any | null>(null);
  const [productName, setProductName] = useState("");
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null);

  const styles = watch();
  const screens = watch("screens");

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setValue("logoUrl", reader.result as string);
    reader.readAsDataURL(file);
  };

  function handleProductTyping(text: string) {
    setProductName(text);
  }

  function handleProductSelect(prod: any) {
    setProductName(prod.description);
    setSelectedProduct(prod);
    setValue("productId", prod.id);
  }

  const onSubmit = async (data: IJourney) => {
    if (Journey) return handleSave(data, true, Journey.id);

    const already = await checkIfExists(data.clientId, data.productId);

    if (!already) {
      handleSave(data, false);
    } else {
      setPendingData(data);
      setShowConfirm(true);
    }
  };

  const handleConfirmReplace = () => {
    if (pendingData) handleSave(pendingData, false);
    setShowConfirm(false);
    setPendingData(null);
  };

  useEffect(() => {
    if (Journey) {
      setValue("clientName", Journey.client?.corporateName || "");
      setValue("clientId", Journey.client?.id || 0);
      setValue("productName", Journey.product?.description || "");
      setValue("productId", Journey.product?.id || 0);
      setProductName(Journey.product?.description || "");

      const fullLogoUrl = resolveLogoUrl(Journey.logoUrl);

      setValue("logoUrl", fullLogoUrl || "");

      setValue("backgroundColor", Journey.backgroundColor || "");
      setValue("labelColor", Journey.labelColor || "");
      setValue("textColor", Journey.textColor || "");
      setValue("buttonColor", Journey.buttonColor || "#78E01D");
      setValue("buttonLabelColor", Journey.buttonLabelColor || "#ffffff");
      setValue(
        "screens",
        Journey.screens.length > 0 ? Journey.screens : defaultScreens
      );
      setValue("initialText", Journey.initialText || "");
      setValue("finalText", Journey.finalText || "");
      setValue("redirectLink", Journey.redirectLink || "");
    }
  }, [Journey]);

  return (
    <div>
      <div
        className="flex-grow-1 p-4 d-flex"
        style={{
          backgroundColor: "#ffffff",
          borderTopLeftRadius: "40px",
          borderBottomLeftRadius: "40px",
          padding: "32px",
          boxShadow: "0 0 12px rgba(0,0,0,0.05)",
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)} className="w-50 pr-4">
          <input
            type="hidden"
            {...register("clientId", { required: "Cliente é obrigatório" })}
          />
          <input
            type="hidden"
            {...register("productId", { required: "Produto é obrigatório" })}
          />
          <div className="row mb-5">
            <div className="col-md-6 form-group">
              <AutoCompleteClient
                label="Cliente:*"
                value={watch("clientName")}
                onChange={(v) => {
                  setValue("clientName", v);
                  setSelectedClient(null);
                }}
                onSelect={(cli) => {
                  setValue("clientName", cli.corporateName);
                  setValue("clientId", cli.id);
                  setSelectedClient(cli);
                  setProductName("");
                  setSelectedProduct(null);
                }}
              />
              {errors.clientId && (
                <small className="text-danger">{errors.clientId.message}</small>
              )}
            </div>
            <div className="col-md-6 form-group">
              <AutoCompleteProduct
                label="Produto:*"
                clientId={selectedClient?.id}
                value={productName}
                onChange={handleProductTyping}
                onSelect={handleProductSelect}
              />
              {errors.productId && (
                <small className="text-danger">
                  {errors.productId.message}
                </small>
              )}
            </div>
          </div>

          <div className="d-flex flex-column gap-4">
            <div className="form-group">
              <label>Logo do cliente:</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label>Cor de fundo:</label>
              <input
                type="color"
                className="form-control"
                {...register("backgroundColor")}
              />
            </div>

            <div className="form-group">
              <label>Cor do texto:</label>
              <input
                type="color"
                className="form-control"
                {...register("textColor")}
              />
            </div>

            <div className="form-group">
              <label>Cor do botão:</label>
              <input
                type="color"
                className="form-control"
                {...register("buttonColor")}
              />
            </div>

            <div className="form-group">
              <label>Cor do texto do botão:</label>
              <input
                type="color"
                className="form-control"
                {...register("buttonLabelColor")}
              />
            </div>
            <Controller
              control={control}
              name="screens"
              render={({ field }) => (
                <ScreenSelector value={field.value} onChange={field.onChange} />
              )}
            />
            <div className="col-md-12 form-group">
              <StyledLabel>Texto inicial:</StyledLabel>

              <input
                style={{ borderRadius: "15px", padding: "10px" }}
                className="form-control"
                type="text"
                {...register("initialText")}
              />
            </div>
            <div className="col-md-12 form-group">
              <StyledLabel>Texto final:</StyledLabel>
              <input
                style={{ borderRadius: "15px", padding: "10px" }}
                className="form-control"
                type="text"
                {...register("finalText")}
              />
            </div>
            <div className="col-md-12 form-group">
              <StyledLabel>Link de redirecionamento:</StyledLabel>
              <input
              placeholder="ex: https://b7inovacao.com.br/"
                style={{ borderRadius: "15px", padding: "10px" }}
                className="form-control"
                type="text"
                {...register("redirectLink")}
              />
            </div>
          </div>

          <Button
            type="submit"
            variant="primary"
            style={{
              backgroundColor: "#78E01D",
              borderColor: "#78E01D",
              fontWeight: 500,
              marginTop: 24,
            }}
          >
            {isSaving ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Carregando...
              </>
            ) : (
              "Salvar Configurações"
            )}
          </Button>
        </form>

        <div className="w-50 d-flex justify-content-center align-items-center">
          <PhonePreview styles={styles} logoUrl={styles.logoUrl} />
        </div>
      </div>

      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Jornada já existente</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Já existe uma jornada para esse cliente/produto.
          <br />
          Deseja substituí-la pelos novos dados?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirm(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleConfirmReplace}>
            Substituir
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default JourneyUIConfig;
